import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  useNavigate,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { MotionConfig } from "motion/react";

import appCss from "../styles.css?url";
import { brand, sameAsUrls } from "../config/brand";

// Social + directory profile URLs for JSON-LD `sameAs`. Sourced from the single
// brand config (src/config/brand.ts) so they're edited in one place — includes
// the Google Business Profile once its placeholder there is filled in. Empty
// placeholders are filtered out, so nothing invented ever ships.
const SOCIAL_LINKS = sameAsUrls;
import { reportLovableError } from "../lib/lovable-error-reporting";
import { ThemeProvider } from "../lib/theme-context";
import Header from "../layouts/parts/Header";
import Footer from "../layouts/parts/Footer";
import CookieBannerErrorBoundary from "../components/CookieBannerErrorBoundary";
import CookieBanner from "../components/CookieBanner";
import { SessionProvider } from "../lib/auth/auth-client";
import IncomingCallListener from "../components/messaging/IncomingCallListener";
import InstallAppPrompt from "../components/InstallAppPrompt";
import UpdateAvailableToast from "../components/UpdateAvailableToast";
import { Toaster } from "../components/ui/sonner";
import { WebMcpProvider } from "../components/mcp/WebMcpProvider";
import { GlossaryEnhancer } from "../components/GlossaryEnhancer";

// Google Fonts stylesheet. Loaded NON-render-blocking (see head() below): we
// preconnect + preload it, then attach it via a tiny inline script using the
// media="print" → onload swap trick so it never blocks first paint. Combined
// with `display=swap`, text paints immediately in the fallback face and swaps
// to the brand fonts once the sheet arrives.
const FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap";


function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "google-site-verification", content: "fuPUk7Q0rWkiQhzjXwWURsoxFdt3jYCsLIIv602GaTE" },
      {
        title:
          "Digitally Baffled — UK Digital Transformation Consultancy",
      },
      {
        name: "description",
        content:
          "The one partner that manages your business's entire digital setup — website, client portal & app, marketing, tools and ongoing support — consolidated and run for you, in plain English.",
      },
      { name: "theme-color", content: "#5048E5" },
      { name: "color-scheme", content: "dark light" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "apple-mobile-web-app-title", content: "Digitally Baffled" },
      {
        property: "og:title",
        content: "Digitally Baffled LTD — Your Managed Digital Partner",
      },
      {
        property: "og:description",
        content:
          "The one partner that manages your business's entire digital setup — website, client portal & app, marketing, tools and ongoing support — consolidated and run for you, in plain English.",
      },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://digitallybaffled.com/__l5e/assets-v1/aabeccab-688e-44b7-96b1-aaf70d6a2a8d/social-share-cover.png" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://digitallybaffled.com/__l5e/assets-v1/aabeccab-688e-44b7-96b1-aaf70d6a2a8d/social-share-cover.png" },
      { name: "twitter:title", content: "Digitally Baffled LTD — Your Managed Digital Partner" },
      { name: "twitter:description", content: "The one partner that manages your business's entire digital setup — website, client portal & app, marketing, tools and ongoing support — consolidated and run for you, in plain English." },
    ],
    links: [
      { rel: "icon", type: "image/x-icon", sizes: "any", href: "https://digitallybaffled.com/favicon.ico" },
      { rel: "icon", type: "image/png", sizes: "48x48", href: "https://digitallybaffled.com/favicon-48x48.png" },
      { rel: "icon", type: "image/png", sizes: "96x96", href: "https://digitallybaffled.com/favicon-96x96.png" },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "https://digitallybaffled.com/apple-touch-icon.png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      // Preload the font stylesheet so its bytes start downloading immediately,
      // but do NOT link it as a blocking <link rel="stylesheet"> — that request
      // was render-blocking (~640ms). The inline script in `scripts` below
      // attaches it via the media="print" → onload swap so it never blocks the
      // first paint; `display=swap` means text shows instantly in the fallback.
      { rel: "preload", as: "style", href: FONTS_HREF },
      // First-party app CSS is critical for correct first paint (prevents FOUC
      // and layout shift), so it stays render-blocking by design.
      { rel: "stylesheet", href: appCss },
    ],
    scripts: [
      {
        // Anti-flash: apply the stored theme, or fall back to the visitor's
        // OS `prefers-color-scheme` when they haven't chosen one, before first
        // paint. Kept in sync with ThemeProvider.
        children:
          "(function(){try{var s=localStorage.getItem('db-theme');var t=(s==='light'||s==='dark')?s:(window.matchMedia&&window.matchMedia('(prefers-color-scheme: light)').matches?'light':'dark');var e=document.documentElement;e.setAttribute('data-theme',t);var b=t==='light'?'hsl(240 30% 98%)':'hsl(228 45% 9%)';e.style.backgroundColor=b;e.style.colorScheme=t==='light'?'light':'dark';}catch(e){document.documentElement.setAttribute('data-theme','dark');document.documentElement.style.backgroundColor='hsl(228 45% 9%)';document.documentElement.style.colorScheme='dark';}})();",
      },
      {
        // Attach the Google Fonts stylesheet without blocking render.
        children:
          "(function(){var l=document.createElement('link');l.rel='stylesheet';l.media='print';l.href=" +
          JSON.stringify(FONTS_HREF) +
          ";l.onload=function(){this.media='all'};document.head.appendChild(l);})();",
      },


      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://digitallybaffled.com/#organization",
              name: "Digitally Baffled LTD",
              legalName: "Digitally Baffled LTD",
              url: "https://digitallybaffled.com",
              email: "hello@digitallybaffled.com",
              telephone: "+447511683542",
              description:
                "Managed service provider (MSP) for small-business digital operations. We consolidate and manage the entire digital setup — websites, client portal & app, marketing, tools and ongoing support — as one partnership, in plain English.",
              foundingDate: "2024",
              founder: { "@id": "https://digitallybaffled.com/#morgan-antell" },
              taxID: "17238376",
              logo: {
                "@type": "ImageObject",
                "@id": "https://digitallybaffled.com/#logo",
                url: "https://digitallybaffled.com/__l5e/assets-v1/f0bdc5e9-161d-4eba-9b27-66e9f15d47b0/site-header-logo.png",
                width: 400,
                height: 60,
              },
              image: { "@id": "https://digitallybaffled.com/#logo" },
              address: {
                "@type": "PostalAddress",
                addressLocality: "Ringwood",
                addressRegion: "Hampshire",
                postalCode: "BH24",
                addressCountry: "GB",
              },
              areaServed: [
                { "@type": "Country", name: "United Kingdom" },
                { "@type": "AdministrativeArea", name: "Worldwide" },
              ],
              sameAs: SOCIAL_LINKS,
            },
            {
              "@type": "Person",
              "@id": "https://digitallybaffled.com/#morgan-antell",
              name: "Morgan Antell",
              jobTitle: "Founder & Digital Transformation Consultant",
              url: "https://digitallybaffled.com/about",
              worksFor: { "@id": "https://digitallybaffled.com/#organization" },
              knowsAbout: [
                "Digital Transformation",
                "Web Design",
                "App Development",
                "SEO",
                "Answer Engine Optimisation (AEO)",
                "Generative Engine Optimisation (GEO)",
                "Google Ads",
                "Facebook Ads",
                "Instagram Ads",
                "Social Media Marketing",
                "Google Business Profile",
                "GDPR Compliance",
                "Small Business Consultancy",
                "UK Company Formation",
                "Brand Identity Design",
              ],
              sameAs: SOCIAL_LINKS,
            },
            {
              "@type": "WebSite",
              "@id": "https://digitallybaffled.com/#website",
              url: "https://digitallybaffled.com",
              name: "Digitally Baffled",
              description:
                "Managed digital partner for UK small businesses — websites, client portal & app, marketing, tools and ongoing support, consolidated and run as one partnership.",
              publisher: { "@id": "https://digitallybaffled.com/#organization" },
              inLanguage: "en-GB",
              potentialAction: {
                "@type": "SearchAction",
                target: {
                  "@type": "EntryPoint",
                  urlTemplate:
                    "https://digitallybaffled.com/blog?q={search_term_string}",
                },
                "query-input": "required name=search_term_string",
              },
            },
            {
              "@type": ["LocalBusiness", "ProfessionalService"],
              "@id": "https://digitallybaffled.com/#localbusiness",
              name: "Digitally Baffled LTD",
              description:
                "Managed service provider (MSP) for small-business digital operations — one partner running websites, client portal & app, marketing, tools and ongoing support as a single, consolidated setup.",
              url: "https://digitallybaffled.com",
              telephone: "+447511683542",
              email: "hello@digitallybaffled.com",
              image: { "@id": "https://digitallybaffled.com/#logo" },
              parentOrganization: { "@id": "https://digitallybaffled.com/#organization" },
              priceRange: "££",
              address: {
                "@type": "PostalAddress",
                streetAddress: "Ringwood",
                addressLocality: "Ringwood",
                addressRegion: "Hampshire",
                postalCode: "BH24",
                addressCountry: "GB",
              },
              geo: { "@type": "GeoCoordinates", latitude: 50.8454, longitude: -1.7895 },
              areaServed: [
                { "@type": "Country", name: "United Kingdom" },
                { "@type": "AdministrativeArea", name: "Worldwide" },
              ],
              openingHoursSpecification: [
                {
                  "@type": "OpeningHoursSpecification",
                  dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                  opens: "09:00",
                  closes: "18:00",
                },
              ],
              aggregateRating: {
                "@type": "AggregateRating",
                ratingValue: "5",
                reviewCount: "1",
                bestRating: "5",
                worstRating: "1",
              },
              review: [
                {
                  "@type": "Review",
                  author: {
                    "@type": "Person",
                    name: "Justin Kirby",
                    description: "Founder, Eclipse Paving",
                  },
                  reviewRating: {
                    "@type": "Rating",
                    ratingValue: "5",
                    bestRating: "5",
                    worstRating: "1",
                  },
                  reviewBody:
                    "From zero online presence to a fully managed digital operation. Morgan and the team explained everything in plain English, built us a site we're proud of, and the leads have been coming in ever since.",
                  datePublished: "2025-01-01",
                },
              ],
              sameAs: SOCIAL_LINKS,
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html
      lang="en"
      suppressHydrationWarning
      // Paint the brand navy (and a dark color-scheme) on the very first frame
      // via inline styles in the SSR'd markup. This runs before any CSS file or
      // the anti-flash <script> executes, eliminating the black canvas backdrop
      // the browser would otherwise show on refresh. The inline script below
      // corrects this to light for users who chose the light theme.
      style={{ backgroundColor: "hsl(228 45% 9%)", colorScheme: "dark" }}
    >




      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function ClientCookieBanner() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;
  return (
    <CookieBannerErrorBoundary>
      <CookieBanner />
    </CookieBannerErrorBoundary>
  );
}

function ClientIncomingCalls() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;
  return <IncomingCallListener />;
}

function ClientInstallPrompt() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;
  return <InstallAppPrompt />;
}

/** Initialises native-only enhancements; a no-op on web. */
function NativeAppBootstrap() {
  const navigate = useNavigate();
  useEffect(() => {
    // Expose a client-side navigate so the native deep-link handler can route
    // without a full page reload (keeps app state). Web ignores this.
    (window as unknown as { __appNavigate?: (to: string) => void }).__appNavigate = (
      to: string,
    ) => navigate({ to, replace: false });
    import("../lib/native/capacitor")
      .then((m) => m.initNativeApp())
      .catch(() => {});
  }, [navigate]);
  return null;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const isPortal = pathname.startsWith("/dashboard");
  const isAdmin = pathname.startsWith("/admin");
  const isAdminSection = pathname === "/tools" || pathname === "/branding-gallery";
  const isProfile = pathname === "/profile" || pathname === "/complete-profile";
  // Hide the global marketing header inside portals — they have their own
  // self-contained chrome (side rail + top bar). Mirrors the footer rule.
  const showHeader = !isPortal && !isAdmin && !isAdminSection && !isProfile;
  const showFooter = !isPortal && !isAdmin && !isAdminSection && !isProfile;

  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <ThemeProvider>
          {/* Global: every framer-motion animation respects the user's
              prefers-reduced-motion setting by rendering statically. */}
          <MotionConfig reducedMotion="user">
          <div className="flex min-h-screen flex-col bg-background">
            {showHeader && <Header />}
            {/* Required: nested routes render here. */}
            <Outlet />
            {showFooter && <Footer />}
          </div>
          <ClientCookieBanner />
          <ClientIncomingCalls />
          <ClientInstallPrompt />
          <NativeAppBootstrap />
          <UpdateAvailableToast />
          <WebMcpProvider />
          <GlossaryEnhancer />
          <Toaster />


          </MotionConfig>
        </ThemeProvider>
      </SessionProvider>
    </QueryClientProvider>
  );
}
